/**
 * Partner/Affiliate Service
 * Handles partner registration, login, and referral code validation
 */

import apiClient, { ApiError } from "./client";
import { ENDPOINTS } from "./config";

// Partner registration form data
export interface PartnerRegisterData {
  name: string;
  email: string;
  phone: string;
  company_name?: string;
  password: string;
  referral_code?: string; // If referred by existing partner
  terms_accepted: boolean;
}

// Partner login data
export interface PartnerLoginData {
  email: string;
  password: string;
}

// Partner registration response
export interface PartnerRegisterResponse {
  success: boolean;
  message: string;
  partner_id?: string;
  referral_code?: string;
  referral_link?: string;
}

// Partner login response
export interface PartnerLoginResponse {
  success: boolean;
  message: string;
  access_token?: string;
  partner?: {
    id: string;
    name: string;
    email: string;
    referral_code: string;
  };
  dashboard_url?: string; // e.g., "https://partner-name.axoixdashboard.com"
}

// Referral code validation response
export interface ReferralValidationResponse {
  valid: boolean;
  partner_name?: string;
  company?: string;
  message?: string;
}

/**
 * Register a new partner
 */
export const registerPartner = async (
  data: PartnerRegisterData
): Promise<PartnerRegisterResponse> => {
  try {
    // Validate required fields
    if (!data.name || !data.email || !data.phone || !data.password) {
      throw new Error("Missing required fields");
    }

    if (!data.terms_accepted) {
      throw new Error("You must accept the terms and conditions");
    }

    const response = await apiClient.post<PartnerRegisterResponse>(
      ENDPOINTS.PARTNERS.REGISTER,
      {
        ...data,
        created_at: new Date().toISOString(),
        source: "marketing_website",
      }
    );

    return response.data;
  } catch (error) {
    const apiError = error as ApiError;

    // Handle specific errors
    if (apiError.status === 409) {
      throw new Error("This email is already registered as a partner");
    }
    if (apiError.status === 400) {
      throw new Error(apiError.message || "Invalid registration data");
    }
    if (apiError.status === 422) {
      throw new Error("Invalid referral code provided");
    }

    throw new Error(apiError.message || "Failed to register partner");
  }
};

/**
 * Partner login
 */
export const loginPartner = async (
  data: PartnerLoginData
): Promise<PartnerLoginResponse> => {
  try {
    if (!data.email || !data.password) {
      throw new Error("Email and password are required");
    }

    const response = await apiClient.post<PartnerLoginResponse>(
      ENDPOINTS.PARTNERS.LOGIN,
      data
    );

    return response.data;
  } catch (error) {
    const apiError = error as ApiError;

    // Handle specific errors
    if (apiError.status === 401) {
      throw new Error("Invalid email or password");
    }
    if (apiError.status === 403) {
      throw new Error("Your partner account is suspended. Please contact support.");
    }

    throw new Error(apiError.message || "Login failed");
  }
};

/**
 * Validate a referral code
 * Used on signup page to show "Referred by: Partner Name"
 */
export const validateReferralCode = async (
  code: string
): Promise<ReferralValidationResponse> => {
  try {
    if (!code || code.trim().length === 0) {
      return { valid: false, message: "No referral code provided" };
    }

    const response = await apiClient.get<ReferralValidationResponse>(
      `${ENDPOINTS.PARTNERS.VALIDATE_CODE}/${code.trim().toUpperCase()}`
    );

    return response.data;
  } catch (error) {
    // If API fails, return invalid (graceful degradation)
    return {
      valid: false,
      message: "Unable to validate referral code",
    };
  }
};

/**
 * Check API health before form submission
 * Returns true if API is available
 */
export const checkPartnerApiHealth = async (): Promise<boolean> => {
  try {
    // Try to hit the validation endpoint with a dummy code
    await apiClient.get(`${ENDPOINTS.PARTNERS.VALIDATE_CODE}/HEALTH_CHECK`, {
      timeout: 5000,
    });
    return true;
  } catch {
    return false;
  }
};

// Commission structure shape returned by /api/v1/partners/public/commission-structure
export interface CommissionTier {
  name: string;
  min_referrals: number;
  rate: number;
}

export interface CommissionStructure {
  tiers: CommissionTier[];
  directReferralMin: number;
  directReferralMax: number;
  l1OverrideRate: number;
  l2OverrideRate: number;
  longTermBonus: number;
  enableMultilevel: boolean;
  minimumPayoutAmount: number;
  payoutProcessingDays: number;
  commissionLockDays: number;
  referralCookieDays: number;
  requireFirstPayment: boolean;
  programActive: boolean;
  newRegistrationsOpen: boolean;
}

// Fallback used when the API is unreachable — mirrors the mothership DEFAULT_PARTNER_SETTINGS
const FALLBACK_COMMISSION_STRUCTURE: CommissionStructure = {
  tiers: [
    { name: "Starter", min_referrals: 0, rate: 10 },
    { name: "Bronze", min_referrals: 50, rate: 10.5 },
    { name: "Silver", min_referrals: 100, rate: 11 },
    { name: "Gold", min_referrals: 200, rate: 11.5 },
    { name: "Platinum", min_referrals: 500, rate: 12 },
  ],
  directReferralMin: 10,
  directReferralMax: 12,
  l1OverrideRate: 2,
  l2OverrideRate: 1,
  longTermBonus: 2,
  enableMultilevel: true,
  minimumPayoutAmount: 500,
  payoutProcessingDays: 7,
  commissionLockDays: 30,
  referralCookieDays: 30,
  requireFirstPayment: true,
  programActive: true,
  newRegistrationsOpen: true,
};

/**
 * Fetch the current partner commission structure from the mothership.
 * Public (no auth). When the admin changes rates in /mothership/partners/settings
 * the marketing page reflects them immediately on next fetch.
 * Returns the fallback if the API is unreachable.
 */
export const fetchCommissionStructure = async (): Promise<CommissionStructure> => {
  try {
    const response = await apiClient.get<CommissionStructure>(
      "/api/v1/partners/public/commission-structure",
      { timeout: 8000 }
    );
    return response.data;
  } catch {
    return FALLBACK_COMMISSION_STRUCTURE;
  }
};

const partnerService = {
  registerPartner,
  loginPartner,
  validateReferralCode,
  checkPartnerApiHealth,
  fetchCommissionStructure,
};

export default partnerService;
